import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import {
  getRevenueReportsService,
  getRecurringRevenueService,
  getUserAcquiredService,
  getUsersByStateService,
  getUsersByAgeService,
  getMostLikedPropertiesService,
  getMostTradedPropertiesService,
  getMostAppreciatedService,
} from '../../services/reports.service';

const initialState = {
  reportsList: {
    totalCount: 0,
    totalPage: 0,
    reportsData: [],
  },
};

export const getRevenueReports = createAsyncThunk(
  `REVENUE_REPORT`,
  async (
    { page, limit, sortField, sortType, duration },
    { rejectWithValue }
  ) => {
    try {
      const response = await getRevenueReportsService(
        page,
        limit,
        sortField,
        sortType,
        duration
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getRecurringRevenueReports = createAsyncThunk(
  `RECURRING_REVENUE_REPORT`,
  async (
    { page, limit, sortField, sortType, duration },
    { rejectWithValue }
  ) => {
    try {
      const response = await getRecurringRevenueService(
        page,
        limit,
        sortField,
        sortType,
        duration
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getUserAcquiredReports = createAsyncThunk(
  `USER_ACQUIRED_REPORT`,
  async (
    { page, limit, sortField, sortType, duration },
    { rejectWithValue }
  ) => {
    try {
      const response = await getUserAcquiredService(
        page,
        limit,
        sortField,
        sortType,
        duration
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getUsersByStateReports = createAsyncThunk(
  `USERS_BY_STATE_REPORTS`,
  async (
    { page, limit, sortField, sortType, duration },
    { rejectWithValue }
  ) => {
    try {
      const response = await getUsersByStateService(
        page,
        limit,
        sortField,
        sortType,
        duration
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getUsersByAgeReports = createAsyncThunk(
  `USERS_BY_AGE_REPORT`,
  async (
    { page, limit, sortField, sortType, duration },
    { rejectWithValue }
  ) => {
    try {
      const response = await getUsersByAgeService(
        page,
        limit,
        sortField,
        sortType,
        duration
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getMostLikedProperties = createAsyncThunk(
  `MOST_LIKED_PROPERTIES_REPORT`,
  async (
    { page, limit, sortField, sortType, duration },
    { rejectWithValue }
  ) => {
    try {
      const response = await getMostLikedPropertiesService(
        page,
        limit,
        sortField,
        sortType,
        duration
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getMostTradedProperties = createAsyncThunk(
  `MOST_TRADED_PROPERTIES_REPORT`,
  async (
    { page, limit, sortField, sortType, duration },
    { rejectWithValue }
  ) => {
    try {
      const response = await getMostTradedPropertiesService(
        page,
        limit,
        sortField,
        sortType,
        duration
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getMostAppreciatedProperties = createAsyncThunk(
  `MOST_APPRECIATED_PROPERTIES_REPORT`,
  async (
    { page, limit, sortField, sortType, duration },
    { rejectWithValue }
  ) => {
    try {
      const response = await getMostAppreciatedService(
        page,
        limit,
        sortField,
        sortType,
        duration
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

const reportsSlice = createSlice({
  name: 'reportsSlice',
  initialState,
  reducers: {
    resetReports: (state) => {
      state.reportsList = {
        totalCount: 0,
        totalPage: 0,
        reportsData: [],
      };
    },
  },
  extraReducers: (builder) => {
    // Revenue report
    builder
      .addCase(getRevenueReports.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, revenueData } = payload?.data?.data;
          state.reportsList = {
            totalCount: count ?? 0,
            totalPage: totalPage ?? 0,
            reportsData: [...(revenueData ?? [])],
          };
        }
      })
      .addCase(getRevenueReports.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Recurring Revenue report
    builder
      .addCase(getRecurringRevenueReports.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, recurringRevenueData } =
            payload?.data?.data;
          state.reportsList = {
            totalCount: count ?? 0,
            totalPage: totalPage ?? 0,
            reportsData: [...(recurringRevenueData ?? [])],
          };
        }
      })
      .addCase(getRecurringRevenueReports.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Users Acquired Report
    builder
      .addCase(getUserAcquiredReports.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, users } = payload?.data?.data;
          state.reportsList = {
            totalCount: count ?? 0,
            totalPage: totalPage ?? 0,
            reportsData: [...(users ?? [])],
          };
        }
      })
      .addCase(getUserAcquiredReports.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Users by state report
    builder
      .addCase(getUsersByStateReports.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, stateUserData } = payload?.data?.data;
          state.reportsList = {
            totalCount: count ?? 0,
            totalPage: totalPage ?? 0,
            reportsData: [...(stateUserData ?? [])],
          };
        }
      })
      .addCase(getUsersByStateReports.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Users by age report
    builder
      .addCase(getUsersByAgeReports.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, ageGroup } = payload?.data?.data;
          state.reportsList = {
            totalCount: count ?? 0,
            totalPage: totalPage ?? 0,
            reportsData: [...(ageGroup ?? [])],
          };
        }
      })
      .addCase(getUsersByAgeReports.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Most liked properties
    builder
      .addCase(getMostLikedProperties.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, mostLikedPropertyData } =
            payload?.data?.data;
          state.reportsList = {
            totalCount: count ?? 0,
            totalPage: totalPage ?? 0,
            reportsData: [...(mostLikedPropertyData ?? [])],
          };
        }
      })
      .addCase(getMostLikedProperties.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Most traded properties
    builder
      .addCase(getMostTradedProperties.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, mostTradedProperty } = payload?.data?.data;
          state.reportsList = {
            totalCount: count ?? 0,
            totalPage: totalPage ?? 0,
            reportsData: [...(mostTradedProperty ?? [])],
          };
        }
      })
      .addCase(getMostTradedProperties.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Most appreciated properties
    builder
      .addCase(getMostAppreciatedProperties.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, mostAppreciatedPropertyData } =
            payload?.data?.data;
          state.reportsList = {
            totalCount: count ?? 0,
            totalPage: totalPage ?? 0,
            reportsData: [...(mostAppreciatedPropertyData ?? [])],
          };
        }
      })
      .addCase(getMostAppreciatedProperties.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });
  },
});

export const { resetReports } = reportsSlice.actions;

export default reportsSlice.reducer;
